import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminNewsletterPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  return `${base}/uploads/newsletters/images/${sanitized}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const NewsletterSkeleton = () => (
  <div className="AN-skeleton">
    <div className="AN-skeleton-image" />
    <div className="AN-skeleton-body">
      <div className="AN-skeleton-line AN-skeleton-line--short" />
      <div className="AN-skeleton-line AN-skeleton-line--medium" />
      <div className="AN-skeleton-line AN-skeleton-line--long" />
      <div className="AN-skeleton-line AN-skeleton-line--medium" />
    </div>
  </div>
);

const StatsCard = ({ icon, label, value, variant }) => (
  <div className="AN-stat-card">
    <div className={classNames('AN-stat-icon', `AN-stat-icon--${variant}`)}>
      <i className={`bi ${icon}`} aria-hidden="true" />
    </div>
    <div className="AN-stat-content">
      <div className="AN-stat-value">{value}</div>
      <div className="AN-stat-label">{label}</div>
    </div>
  </div>
);

const EmptyState = ({ onCreate, showArchived }) => (
  <div className="AN-empty-state">
    <i className="bi-envelope-paper AN-empty-icon" />
    <h5 className="AN-empty-title">
      {showArchived ? 'Aún no hay newsletters archivados' : 'Aún no hay newsletters'}
    </h5>
    <p className="AN-empty-description">
      {showArchived
        ? 'No hay newsletters archivados en este momento.'
        : 'Crea tu primer newsletter para comenzar a comunicarte con tus suscriptores.'}
    </p>
    {!showArchived && (
      <Button variant="primary" onClick={onCreate}>
        <i className="bi-plus-circle me-2" />
        Crear newsletter
      </Button>
    )}
  </div>
);

const AdminNewsletterInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  // Verificar permisos
  const hasPanelNewsletter = useMemo(() => hasPermission(user, PERMISSION_IDS.PANEL_NEWSLETTER), [user]);
  const hasCrearNewsletter = useMemo(() => hasPermission(user, PERMISSION_IDS.CREAR_NEWSLETTER), [user]);
  const hasEditarNewsletter = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_NEWSLETTER), [user]);
  const hasPublicarNewsletter = useMemo(() => hasPermission(user, PERMISSION_IDS.PUBLICAR_NEWSLETTER), [user]);
  const hasArchivarNewsletter = useMemo(() => hasPermission(user, PERMISSION_IDS.ARCHIVAR_NEWSLETTER), [user]);
  const hasVerArchivados = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_NEWSLETTERS_ARCHIVADOS), [user]);
  const hasEstadisticas = useMemo(() => hasPermission(user, PERMISSION_IDS.ESTADISTICAS_NEWSLETTER), [user]);

  const [newsletters, setNewsletters] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    archived: 0,
    draft: 0,
    subscribers: 0,
    views: 0,
  });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  // Verificar acceso
  useEffect(() => {
    if (user && !hasPanelNewsletter) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de newsletters.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasPanelNewsletter, navigate, notify]);

  const fetchStats = async (isRefresh = false) => {
    if (!token || !hasEstadisticas) return;
    if (!isRefresh) setLoadingStats(true);
    try {
      const response = await fetch(buildApiUrl('/api/newsletters/stats/summary'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error al cargar estadísticas (${response.status})`);
      }
      const data = await response.json();
      setStats(data);
    } catch (error) {
      logError('[AdminNewsletterPage] Error fetching stats:', error);
      if (error.message && !error.message.includes('429')) {
        notify({
          title: 'No pudimos obtener las estadísticas',
          message: error.message,
          variant: 'error',
        });
      }
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchNewsletters = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoading(true);
    try {
      const url = showArchived
        ? buildApiUrl('/api/newsletters/admin/archived')
        : buildApiUrl('/api/newsletters/admin/all');
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error al cargar newsletters (${response.status})`);
      }
      const data = await response.json();
      setNewsletters(Array.isArray(data) ? data : []);
    } catch (error) {
      logError('[AdminNewsletterPage] Error fetching newsletters:', error);
      if (error.message && !error.message.includes('429')) {
        notify({
          title: 'No pudimos cargar los newsletters',
          message: error.message,
          variant: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await Promise.all([
          fetchNewsletters(),
          hasEstadisticas && fetchStats(),
        ]);
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showArchived]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      fetchNewsletters(true),
      hasEstadisticas && fetchStats(true),
    ]);
    setIsRefreshing(false);
    notify({
      title: 'Datos actualizados',
      message: 'La lista de newsletters y estadísticas se sincronizaron correctamente.',
      variant: 'success',
    });
  };

  const filteredNewsletters = useMemo(() => {
    if (!search.trim()) return newsletters;
    const term = search.trim().toLowerCase();
    return newsletters.filter((nl) => {
      const title = nl.title || '';
      const description = nl.shortDescription || '';
      return (
        title.toLowerCase().includes(term) ||
        description.toLowerCase().includes(term) ||
        String(nl.id).includes(term)
      );
    });
  }, [newsletters, search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const response = await fetch(buildApiUrl('/api/newsletters'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al crear newsletter');
      }
      const data = await response.json();
      setShowCreateModal(false);
      setNewTitle('');
      notify({
        title: 'Newsletter creado',
        message: 'El newsletter se creó correctamente. Redirigiendo al detalle...',
        variant: 'success',
      });
      await Promise.all([fetchStats(true), fetchNewsletters(true)]);
      // Navegar al detalle del nuevo newsletter
      if (data.newsletter?.id) {
        setTimeout(() => {
          navigate(`/admin/newsletters/${data.newsletter.id}`);
        }, 1000);
      }
    } catch (error) {
      logError('[AdminNewsletterPage] Error creating newsletter:', error);
      notify({
        title: 'Error',
        message: error.message || 'No se pudo crear el newsletter',
        variant: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      const response = await fetch(buildApiUrl(`/api/newsletters/${id}/activate`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al publicar newsletter');
      }
      const data = await response.json();
      notify({
        title: 'Newsletter publicado',
        message: `Se enviaron ${data.sent || 0} emails a los suscriptores`,
        variant: 'success',
      });
      await Promise.all([fetchStats(true), fetchNewsletters(true)]);
    } catch (error) {
      logError('[AdminNewsletterPage] Error publishing newsletter:', error);
      notify({
        title: 'Error',
        message: error.message || 'No se pudo publicar el newsletter',
        variant: 'error',
      });
    }
  };

  const handleArchive = async (id) => {
    try {
      const response = await fetch(buildApiUrl(`/api/newsletters/${id}/archive`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al archivar newsletter');
      }
      notify({
        title: 'Newsletter archivado',
        message: 'El newsletter se archivó correctamente',
        variant: 'success',
      });
      await Promise.all([fetchStats(true), fetchNewsletters(true)]);
    } catch (error) {
      logError('[AdminNewsletterPage] Error archiving newsletter:', error);
      notify({
        title: 'Error',
        message: error.message || 'No se pudo archivar el newsletter',
        variant: 'error',
      });
    }
  };

  if (!hasPanelNewsletter) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="newsletters" onNavigate={(item) => item.path && navigate(item.path)} />
          <main className="admin-main">
            <div className="AN-container">
              <div className="text-center py-5">
                <h5>No tienes acceso a esta sección</h5>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="newsletters"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="AN-container">
            {/* Header */}
            <div className="AN-header">
              <div className="AN-header-content">
                <h1 className="AN-title">
                  <i className="bi-envelope-paper-fill text-primary" />
                  Gestión de Newsletters
                </h1>
                <p className="AN-subtitle">
                  Administra tus newsletters, publica novedades y analiza el alcance
                </p>
              </div>
              <div className="AN-actions">
                {hasVerArchivados && (
                  <Button
                    variant={showArchived ? 'secondary' : 'outline-secondary'}
                    onClick={() => setShowArchived(!showArchived)}
                  >
                    <i className={showArchived ? 'bi-list-ul me-2' : 'bi-archive me-2'} />
                    {showArchived ? 'Ver activos' : 'Ver archivados'}
                  </Button>
                )}
                {hasCrearNewsletter && (
                  <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                    <i className="bi-plus-circle me-2" />
                    Crear Newsletter
                  </Button>
                )}
              </div>
            </div>

            {/* Stats Section */}
            {hasEstadisticas && (
              <section className="AN-stats-section">
                <div className="AN-stats-grid">
                  <StatsCard
                    icon="bi-bullhorn-fill"
                    label="Newsletters activos"
                    value={loadingStats ? '--' : stats.active}
                    variant="success"
                  />
                  <StatsCard
                    icon="bi-eye-fill"
                    label="Vistas totales"
                    value={loadingStats ? '--' : stats.views}
                    variant="info"
                  />
                  <StatsCard
                    icon="bi-people-fill"
                    label="Usuarios suscritos"
                    value={loadingStats ? '--' : stats.subscribers}
                    variant="primary"
                  />
                </div>
              </section>
            )}

            {/* Search Section */}
            <section className="AN-search-section">
              <div className="AN-search-card">
                <div className="AN-search-content">
                  <Form.Control
                    type="search"
                    placeholder="Buscar por título, descripción o ID..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="AN-search-input"
                  />
                  <div className="AN-search-actions">
                    <Button
                      variant="outline-primary"
                      onClick={() => setSearch('')}
                      disabled={!search}
                      size="sm"
                    >
                      Limpiar
                    </Button>
                    <Button variant="primary" onClick={handleRefresh} disabled={isRefreshing} size="sm">
                      {isRefreshing ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Actualizando
                        </>
                      ) : (
                        <>
                          <i className="bi-arrow-repeat me-2" />
                          Actualizar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            {/* Newsletters Section */}
            <section className="AN-newsletters-section">
              {loading ? (
                <div className="AN-newsletters-grid">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <NewsletterSkeleton key={index} />
                  ))}
                </div>
              ) : filteredNewsletters.length === 0 ? (
                <EmptyState onCreate={() => setShowCreateModal(true)} showArchived={showArchived} />
              ) : (
                <div className="AN-newsletters-grid">
                  {filteredNewsletters.map((newsletter) => {
                    const coverUrl = resolveImageUrl(newsletter.coverImage);
                    const status = newsletter.status === 'active'
                      ? { label: 'Activo', variant: 'success' }
                      : newsletter.status === 'archived'
                      ? { label: 'Archivado', variant: 'secondary' }
                      : { label: 'Borrador', variant: 'warning' };

                    return (
                      <div key={newsletter.id} className="AN-newsletter-card">
                        {coverUrl ? (
                          <img src={coverUrl} alt={newsletter.title || 'Newsletter'} className="AN-newsletter-image" />
                        ) : (
                          <div className="AN-newsletter-image-placeholder">
                            <i className="bi-envelope-paper" />
                          </div>
                        )}
                        <div className="AN-newsletter-body">
                          <div className="AN-newsletter-badges">
                            <Badge bg={status.variant}>{status.label}</Badge>
                          </div>
                          <h3 className="AN-newsletter-title">{newsletter.title || 'Sin título'}</h3>
                          <p className="AN-newsletter-description">
                            {newsletter.shortDescription || 'Sin descripción disponible.'}
                          </p>
                          <div className="AN-newsletter-meta">
                            {newsletter.publishedAt && (
                              <span className="AN-newsletter-meta-item">
                                <i className="bi-calendar3 me-1" />
                                {formatDate(newsletter.publishedAt)}
                              </span>
                            )}
                            {newsletter.author && (
                              <span className="AN-newsletter-meta-item">
                                <i className="bi-person me-1" />
                                {newsletter.author.nombre || 'Autor'}
                              </span>
                            )}
                          </div>
                          <div className="AN-newsletter-actions">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={!hasEditarNewsletter}
                              onClick={() => navigate(`/admin/newsletters/${newsletter.id}`)}
                            >
                              <i className="bi-pencil me-1" />
                              Editar
                            </Button>
                            {newsletter.status !== 'active' && (
                              <Button
                                variant="success"
                                size="sm"
                                disabled={!hasPublicarNewsletter}
                                onClick={() => handlePublish(newsletter.id)}
                              >
                                <i className="bi-send me-1" />
                                Publicar
                              </Button>
                            )}
                            {newsletter.status !== 'archived' && (
                              <Button
                                variant="warning"
                                size="sm"
                                disabled={!hasArchivarNewsletter}
                                onClick={() => handleArchive(newsletter.id)}
                              >
                                <i className="bi-archive me-1" />
                                Archivar
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          {/* Modal crear newsletter */}
          <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                <i className="bi-envelope-paper me-2" />
                Crear Newsletter
              </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleCreate}>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre del Newsletter *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Ej: Newsletter de Enero 2024"
                    required
                    autoFocus
                    maxLength={120}
                    disabled={creating}
                  />
                  <Form.Text className="text-muted">
                    El nombre debe ser descriptivo y único.
                  </Form.Text>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                >
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={creating || !newTitle.trim()}>
                  {creating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Creando...
                    </>
                  ) : (
                    <>
                      <i className="bi-check-circle me-2" />
                      Crear
                    </>
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </main>
      </div>
    </div>
  );
};

const AdminNewsletterPage = () => {
  return (
    <AdminNotificationsProvider>
      <AdminNewsletterInner />
    </AdminNotificationsProvider>
  );
};

export default AdminNewsletterPage;
